import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext.tsx';

interface FollowUser {
  id: string;
  username: string;
  first_name: string;
  last_name: string;
  profile_picture?: string;
  user_type: string;
  bio?: string;
  is_following?: boolean; // Add this to track follow status
}

interface FollowingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onViewUser: (userId: string) => void;
  type: 'following' | 'followers';
  userId?: string;
}

export const FollowingModal: React.FC<FollowingModalProps> = ({
  isOpen,
  onClose,
  onViewUser,
  type,
  userId
}) => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<FollowUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [followLoading, setFollowLoading] = useState<{ [key: string]: boolean }>({});

  const targetUserId = userId || currentUser?.id;

  useEffect(() => {
    if (isOpen && targetUserId) {
      fetchUsers();
    }
  }, [isOpen, type, targetUserId]);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('authToken');
      const endpoint = type === 'following' ? 'following' : 'followers';

      const response = await fetch(`https://aztrainersbackend.onrender.com/api/users/${targetUserId}/${endpoint}/`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        const usersWithFollowStatus = (data.users || []).map((user: FollowUser) => ({
          ...user,
          // If this is a "following" list, all users are being followed
          // If this is a "followers" list, use the is_following status from API or default to false
          is_following: type === 'following' ? true : (user.is_following || false)
        }));
        setUsers(usersWithFollowStatus);
      } else if (response.status === 404) {
        setError('User not found');
      } else {
        setError(`Failed to load ${type}`);
      }
    } catch (err) {
      console.error(`Failed to fetch ${type}:`, err);
      setError(`Failed to load ${type}. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async (followUserId: string, isCurrentlyFollowing: boolean) => {
    if (!currentUser || followLoading[followUserId]) return;

    setFollowLoading(prev => ({ ...prev, [followUserId]: true }));

    try {
      const token = localStorage.getItem('authToken');

      const response = await fetch(`https://aztrainersbackend.onrender.com/api/users/${followUserId}/follow/`, {
        method: isCurrentlyFollowing ? 'DELETE' : 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        // Update the user's follow status in the list
        setUsers(prevUsers =>
          prevUsers.map(user =>
            user.id === followUserId
              ? { ...user, is_following: !isCurrentlyFollowing }
              : user
          )
        );
      } else {
        console.error('Failed to toggle follow status');
      }
    } catch (error) {
      console.error('Error toggling follow:', error);
    } finally {
      setFollowLoading(prev => ({ ...prev, [followUserId]: false }));
    }
  };

  const handleUserClick = (clickedUserId: string) => {
    onViewUser(clickedUserId);
    onClose();
  };

  const getFullName = (user: FollowUser) => {
    return `${user.first_name} ${user.last_name}`.trim() || user.username;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white/90 backdrop-blur-sm rounded-lg border border-white/40 shadow-lg max-w-md w-full max-h-[70vh] overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800 capitalize">
            {type} ({users.length})
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E6A756]"></div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-500 mb-4">{error}</p>
              <button
                onClick={fetchUsers}
                className="bg-[#E6A756] text-[#5D4037] px-4 py-2 rounded-full hover:bg-[#D2824C] transition-colors font-medium"
              >
                Try Again
              </button>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-500 mb-2">
                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">No {type} yet</h3>
              <p className="text-gray-500">
                {type === 'following' ? "You're not following anyone yet." : "No followers yet."}
              </p>
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto space-y-3">
              {users.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors group"
                >
                  {/* Profile Picture */}
                  <button
                    onClick={() => handleUserClick(user.id)}
                    className="flex-shrink-0"
                  >
                    <div className="w-12 h-12 bg-gray-300 rounded-full overflow-hidden">
                      {user.profile_picture ? (
                        <img
                          src={user.profile_picture}
                          alt={getFullName(user)}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#E6A756] to-[#D2824C] flex items-center justify-center text-white font-bold">
                          {user.first_name[0]}{user.last_name[0]}
                        </div>
                      )}
                    </div>
                  </button>

                  {/* User Info */}
                  <button
                    onClick={() => handleUserClick(user.id)}
                    className="flex-1 min-w-0 text-left"
                  >
                    <div className="flex items-center space-x-2">
                      <h4 className="font-semibold text-gray-800 truncate">
                        {getFullName(user)}
                      </h4>
                      {user.user_type === 'trainer' && (
                        <span className="bg-[#9CAF88] text-[#FEFCF8] text-xs px-2 py-0.5 rounded-full flex-shrink-0 font-medium">
                          Trainer
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 text-sm">@{user.username}</p>
                    {user.bio && (
                      <p className="text-gray-500 text-xs truncate mt-1">
                        {user.bio}
                      </p>
                    )}
                  </button>

                  {/* Follow/Unfollow Button */}
                  {currentUser && currentUser.id !== user.id && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFollow(user.id, user.is_following || false);
                      }}
                      disabled={followLoading[user.id]}
                      className={`px-4 py-1.5 text-sm rounded-full font-medium transition-colors disabled:opacity-50 flex-shrink-0 ${user.is_following
                          ? 'bg-[#F8F4EE] text-[#5D4037] hover:bg-[#F5EFE7] border border-[#D4C4B0]'
                          : 'bg-[#9CAF88] text-[#FEFCF8] hover:bg-[#7A8471]'
                        }`}
                    >
                      {followLoading[user.id] ? (
                        <div className="flex items-center space-x-1">
                          <div className="w-3 h-3 border border-[#5D4037] border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      ) : (
                        user.is_following ? 'Following' : 'Follow'
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FollowingModal;